import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import { useState } from 'react';
import * as Yup from 'yup';

import { UpdateMemberDataAddress } from 'components/treatments/dialogs/UpdateMemberDataAddress';
import { UpdateMemberDataPhone } from 'components/treatments/dialogs/UpdateMemberDataPhone';
import { handleError } from 'services/Error';
import {
    usePatchMemberAddressMutation,
    usePatchMemberPhoneMutation,
} from 'store/api/scopeAssessorApi';

const Title = 'Update Member Data';

const MemberDetailsTypes = {
    MEMBER_PHONE: 'Member Phone Number',
    MEMBER_ADDRESS: 'Member Address',
};
const MemberDetailsTypeComponentMap = {
    [MemberDetailsTypes.MEMBER_PHONE]: UpdateMemberDataPhone,
    [MemberDetailsTypes.MEMBER_ADDRESS]: UpdateMemberDataAddress,
};

export const UpdateMemberDataDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    memberDetails = undefined,
    username,
    submitCallback,
}) => {
    const [selectedMemberDetails, setSelectedMemberDetails] = useState();
    const SelectedMemberDetailsComponent = MemberDetailsTypeComponentMap[selectedMemberDetails];

    const memberDetailsTypeOptions = [
        MemberDetailsTypes.MEMBER_PHONE,
        MemberDetailsTypes.MEMBER_ADDRESS,
    ];

    const [patchMemberPhone] = usePatchMemberPhoneMutation();

    const [patchMemberAddress] = usePatchMemberAddressMutation();

    return (
        <Formik
            initialValues={{
                memberDetailsType: selectedMemberDetails,
                ...SelectedMemberDetailsComponent?.initialValues({ memberDetails }),
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                memberDetailsType: Yup.string().required(
                    validationStrings.requiredField.errorMessage,
                ),
                ...SelectedMemberDetailsComponent?.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await SelectedMemberDetailsComponent?.onSubmit({
                        treatmentDetails,
                        memberDetails,
                        values,
                        username,
                        patchMemberPhone,
                        patchMemberAddress,
                    });

                    submitCallback();

                    SuccessToast({ message: 'Member data updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update member data. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Update"
                    isPrimaryButtonDisabled={!formik.isValid || formik.isSubmitting}
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="memberDetailsType"
                                label="What Member Details Do You Need To Update?"
                                required
                                values={memberDetailsTypeOptions}
                                onChange={setSelectedMemberDetails}
                            />
                            {SelectedMemberDetailsComponent && (
                                <SelectedMemberDetailsComponent memberDetails={memberDetails} />
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UpdateMemberDataDialog.Title = Title;

UpdateMemberDataDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        payerId: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        firstName: PropTypes.string,
        dateOfBirth: PropTypes.string,
        secondaryPhone: PropTypes.string,
    }),
    username: PropTypes.string.isRequired,
    submitCallback: PropTypes.func.isRequired,
};
